import {
  Alert,
  alertTypes,
  alertVariants,
  NewButton,
  NewButtonVariant,
  Tabs,
} from "@visiblethread/react-components";
import EditModeStatus from "components/EditModeStatus";
import ShareUrlButton from "components/ShareUrlButton";
import EntityActivityPanel from "layout/activityLog/EntityActivityPanel";
import { ENTITY_TYPE } from "layout/documentUploader/constants";
import { PencilLineIcon, Save } from "lucide-react";
import { getOptionsByEntityType } from "pages/utils";
import { array, bool, func, string } from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProposalOutlineByOwner,
  saveProposalOutline,
} from "slices/proposalOutline/actions/outlineActions";
import {
  lockEditing,
  unlockEditing,
} from "slices/proposalOutline/actions/sessionActions";
import {
  resetEditing,
  setIsEditing,
} from "slices/proposalOutline/outlineSlice";
import {
  selectAreAllSectionsLinked,
  selectIsEditing,
  selectIsLockedByAnotherUser,
  selectLockedBy,
  selectOriginalSections,
  selectOutlineCreatedBy,
  selectOutlineData,
  selectOutlineOwner,
  selectProposalOutlineSections,
} from "slices/proposalOutline/selectors";
import { detectOutlineToDocChanges } from "utils/outlineDocSync";

import { COVERAGE_TAB, SECTIONS_TAB } from "../../constants";
import ExportRequirementsButton from "../ExportRequirementsButton";
import ActionsDropdownButton from "./ActionsDropdownButton";
import CombineDocumentsButton from "./CombineDocumentsButton";
import ExportDropdownButton from "./ExportDropdownButton";
import OutlineDocSyncModal from "./OutlineDocSyncModal";
import classes from "./OutlineTableHeader.module.scss";

const OutlineTableHeader = ({
  activeTab,
  onSubmitRegenerate,
  llmAvailable = true,
  isArchived = false,
  onSelectTab,
  tabs = [],
}) => {
  const dispatch = useDispatch();
  const owner = useSelector(selectOutlineOwner);
  const outlineData = useSelector(selectOutlineData);
  const isEditing = useSelector(selectIsEditing);
  const createdBy = useSelector(selectOutlineCreatedBy);
  const isLockedByOtherUser = useSelector(selectIsLockedByAnotherUser);
  const lockedBy = useSelector(selectLockedBy);
  const originalSections = useSelector(selectOriginalSections);
  const currentSections = useSelector(selectProposalOutlineSections);
  const areAllSectionsLinked = useSelector(selectAreAllSectionsLinked);
  const hasNoSections = currentSections.length === 0;
  const canCombine =
    !areAllSectionsLinked && !hasNoSections && !isLockedByOtherUser;

  const isCoverageTab = activeTab === COVERAGE_TAB;
  const tabElements = (tabs || []).map(({ content, value }) => ({
    content,
    onSelect: () => onSelectTab?.(value),
  }));

  const combineDisabledReason = useMemo(() => {
    switch (true) {
      case isLockedByOtherUser:
        return `You cannot combine this outline as ${lockedBy} is currently editing`;
      case areAllSectionsLinked:
        return "You cannot combine this outline as it has already been combined";
      case hasNoSections:
        return "Add at least one section before combining";
      default:
        return null;
    }
  }, [areAllSectionsLinked, hasNoSections, isLockedByOtherUser, lockedBy]);

  const [syncModalState, setSyncModalState] = useState({
    isOpen: false,
    isLoading: false,
    changeResult: null,
  });

  const dispatchOwner = (factory) => {
    if (!owner) {
      return null;
    }
    return dispatch(factory(owner.ownerType, owner.ownerId));
  };

  const onClickCancel = async () => {
    dispatch(resetEditing());
    await dispatchOwner(unlockEditing);
    await dispatchOwner(fetchProposalOutlineByOwner);
  };

  const onClickEdit = async () => {
    const canEdit = await dispatchOwner(lockEditing);
    if (canEdit) {
      dispatch(setIsEditing(true));
    }
  };

  const onClickSave = () => {
    const changeResult = detectOutlineToDocChanges(
      originalSections,
      currentSections
    );

    if (changeResult.hasDocumentChanges) {
      setSyncModalState({
        isOpen: true,
        isLoading: false,
        changeResult,
      });
    } else {
      dispatchOwner(saveProposalOutline);
    }
  };

  const onSyncAndSave = async () => {
    if (!owner) {
      return;
    }
    setSyncModalState((prev) => ({ ...prev, isLoading: true }));
    await dispatch(
      saveProposalOutline(
        owner.ownerType,
        owner.ownerId,
        syncModalState.changeResult.syncPayload
      )
    );
    setSyncModalState({ isOpen: false, isLoading: false, changeResult: null });
  };

  const onSaveOutlineOnly = async () => {
    setSyncModalState((prev) => ({ ...prev, isLoading: true }));
    await dispatchOwner(saveProposalOutline);
    setSyncModalState({ isOpen: false, isLoading: false, changeResult: null });
  };

  const onCancelSync = () => {
    setSyncModalState({ isOpen: false, isLoading: false, changeResult: null });
  };

  const renderHeaderButtons = () => {
    if (isCoverageTab) {
      return (
        <>
          <ShareUrlButton />
          <ExportRequirementsButton />
        </>
      );
    }

    if (isArchived) {
      return (
        <>
          <ShareUrlButton />
          {getOptionsByEntityType(ENTITY_TYPE.PROPOSAL, { isArchived })}
        </>
      );
    }

    if (isEditing) {
      return (
        <>
          <ShareUrlButton />

          <NewButton
            data-testid="cancel-edits-btn"
            onClick={onClickCancel}
            variant={NewButtonVariant.l2}
          >
            Cancel Edits
          </NewButton>

          <NewButton
            data-testid="save-edits-btn"
            onClick={onClickSave}
            variant={NewButtonVariant.l1}
            disabled={hasNoSections}
            leftIcon={<Save size={15} aria-hidden />}
          >
            Save Edits
          </NewButton>
        </>
      );
    }
    return (
      <>
        {outlineData?.id && owner && (
          <EntityActivityPanel
            entityType="OUTLINE"
            entityId={outlineData.id}
            ownerType={owner.ownerType}
            ownerId={owner.ownerId}
          />
        )}

        <ShareUrlButton />

        <CombineDocumentsButton
          disabled={!canCombine}
          disabledReason={combineDisabledReason}
        />

        <ExportDropdownButton />

        <NewButton
          disabled={isLockedByOtherUser}
          data-testid="edit-outline-btn"
          onClick={onClickEdit}
          variant={NewButtonVariant.l1}
          leftIcon={<PencilLineIcon size={15} aria-hidden />}
        >
          Edit Structure
        </NewButton>

        <ActionsDropdownButton
          isArchived={isArchived}
          llmAvailable={llmAvailable}
          onSubmitRegenerate={onSubmitRegenerate}
        />
      </>
    );
  };

  return (
    <>
      {isLockedByOtherUser && (
        <Alert
          variant={alertVariants.warning}
          text={`You can not edit this outline as ${lockedBy} is currently editing`}
          type={alertTypes.inline}
          hideCloseButton
        />
      )}

      <div className={classes["outline_table_header"]}>
        <div className={classes["outline_table_header__tabs"]}>
          {tabElements.length > 0 && (
            <Tabs
              active={activeTab === SECTIONS_TAB ? 0 : 1}
              key={activeTab}
              elements={tabElements}
            />
          )}
        </div>

        <div className={classes["outline_table_header__buttons"]}>
          {renderHeaderButtons()}
        </div>
      </div>

      {!isCoverageTab && (
        <div className={classes["outline_table_meta"]}>
          {!isArchived && (
            <EditModeStatus editMode={isEditing} target="outline" />
          )}
          <em>Created by {createdBy}</em>
        </div>
      )}

      <OutlineDocSyncModal
        isOpen={syncModalState.isOpen}
        isLoading={syncModalState.isLoading}
        affectedDocuments={syncModalState.changeResult?.affectedDocuments || []}
        onSyncAndSave={onSyncAndSave}
        onSaveOutlineOnly={onSaveOutlineOnly}
        onCancel={onCancelSync}
      />
    </>
  );
};

OutlineTableHeader.propTypes = {
  activeTab: string,
  isArchived: bool,
  llmAvailable: bool,
  onSubmitRegenerate: func,
  onSelectTab: func,
  tabs: array,
};

export default OutlineTableHeader;
